import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import {IModule} from "@/model/Locker/IModule";
import {ILocker, IRestrictCellsActionParam} from "@/model/Locker/lockerInterfaces";
import {CellStructureBuilder} from "@/helpers/CellStructureBuilder";
import {IShortLocker} from "@/model/shortInterfaces";
import responseHandler from "@/plugins/errorHandling";
import {IListOrder, IOrder} from "@/model/Order/orderInterfaces";
import Vue from "vue";
import {
  PAGINATION_FIRST_PAGE,
  PAGINATION_DEFAULT_LAST_PAGE,
  PAGINATION_DEFAULT_TOTAL,
  PAGINATION_DEFAULT_PER_PAGE,
} from "@/constants/pagination";

const cellStructureBuilder = new CellStructureBuilder();


interface LockerState {
  lockers: IShortLocker[];
  locker?: ILocker;
  credentials: any;
  availableCells: object;
  screenShotToken: string;
  lockersOrders: IListOrder[];
  packagesToTransfer: IListOrder[];
  errors: IErrors;
  lockersList: IShortLocker[];
  lockersListCurrentPage: number;
  lockersListLastPage: number;
  lockersListTotal: number;
  lockersListPerPage: number;
}

const state: LockerState = {
  lockers: [],
  locker: undefined,
  credentials: {},
  availableCells: {},
  screenShotToken: '',
  lockersOrders: [],
  packagesToTransfer: [],
  errors: {},
  lockersList: [],
  lockersListCurrentPage: PAGINATION_FIRST_PAGE,
  lockersListLastPage: PAGINATION_DEFAULT_LAST_PAGE,
  lockersListTotal: PAGINATION_DEFAULT_TOTAL,
  lockersListPerPage: PAGINATION_DEFAULT_PER_PAGE,
};


const getters: GetterTree<LockerState, RootState> = {
  lockers: (state): IShortLocker[] => state.lockers,
  locker: (state): ILocker => state.locker || {} as ILocker,
  modules: (state): IModule[] => {
    const cells = state.locker ? state.locker.cells : [];
    const lockerId = state.locker ? state.locker.id : 0;
    return cellStructureBuilder.fromCells(cells, lockerId);
  },
  credentials: (state): any => state.credentials,
  availableCells: (state): any => state.availableCells,
  screenShotToken: (state): string => state.screenShotToken,
  lockersOrders: (state): IListOrder[] => state.lockersOrders,
  packagesToTransfer: (state): IListOrder[] => state.packagesToTransfer,
  errors: (state) => state.errors,
  lockersList: (state): IShortLocker[] => state.lockersList,
  lockersListCurrentPage: (state): number => state.lockersListCurrentPage,
  lockersListLastPage: (state): number => state.lockersListLastPage,
  lockersListTotal: (state): number => state.lockersListTotal,
  lockersListPerPage: (state): number => state.lockersListPerPage,
};

const actions: ActionTree<LockerState, RootState> = {
  fetchLocker: ({ commit }, lockerId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId
    }),
    commit,
    'lockerLoaded'
  ),
  fetchCredentials: ({ commit }, lockerId): any => {
    commit('credentialsLoaded', {});
    return responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'lockers/' + lockerId + '/credentials'
      }),
      commit,
      'credentialsLoaded'
    );
  },
  fetchLockers: ({ commit, state }): any => responseHandler.debounce<IShortLocker[]>(
    state.lockers,
    150,
    () => axios({
      url: settings.endpoint + 'lockers/'
    }),
    commit,
    'lockersLoaded'
  ),
  createLocker: (_, data: any): Promise<any> => {
    return axios({
      url: settings.endpoint + 'lockers',
      method: 'POST',
      data,
    }).then((response: any) => {
      if (response && response.data && response.data.data) {
        return response.data.data;
      }
      return response.data;
    });
  },
  fetchLockerThemes: (): Promise<string[]> => {
    return axios({
      url: settings.endpoint + 'lockers/themes',
      method: 'GET',
    }).then((response: any) => {
      const items = response && response.data && response.data.data;
      if (Array.isArray(items)) {
        return items.map((t: any) => t.name).filter(Boolean);
      }
      return [];
    }).catch(() => []);
  },
  updateLocker: ({ commit }, {lockerId, pathPart, data}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + pathPart,
      method: 'PUT',
      data
    }),
    commit,
    'lockerLoaded'
  ),
  rotateScreen: ({ commit }, {lockerId, orientation, resolution}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/rotate-screen',
      method: 'POST',
      data: {orientation, resolution},
    }).then((response): any => ({
      ...response,
      data: {data: {orientation, resolution, applied: true}},
    })),
    commit,
    'lockerScreenConfigSet',
  ),
  disableCell: ({ commit }, {lockerId, cellId, disabled}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/cells/' + cellId + '/disable',
      method: 'PUT',
      data: {
        disabled: !!disabled
      }
    }),
    commit,
    'lockerUpdated'
  ),
  cellAccessible: ({commit}, {lockerId, cellId, accessible}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/cells/' + cellId + '/accessible',
      method: 'PUT',
      data: {
        accessible: !!accessible
      }
    }),
    commit,
    'lockerUpdated'
  ),
  openCell: ({ commit }, {lockerId, cellId}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/cells/' + cellId + '/open',
      method: 'POST'
    }),
    commit,
    'cellOpened',
    null,
    false
  ),
  finalizeCellOrder: ({ commit }, {orderId}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'orders/' + orderId + '/finalize',
      method: 'PUT'
    }),
    commit,
    'orderCollected',
    null,
    false
  ),
  addCellRestriction: ({ commit }, {lockerId, cellId, spId}: IRestrictCellsActionParam): any =>
    responseHandler.handle(
      () => axios({
        url: `${ settings.endpoint }lockers/${ lockerId }/cells/${ cellId }/restrictions/serviceProviders/${ spId }`,
        method: 'POST',
      }),
      commit,
      'lockerUpdated',
    ),
  deleteCellRestriction: ({ commit }, {lockerId, cellId, spId}: IRestrictCellsActionParam): any =>
    responseHandler.handle(
      () => axios({
        url: `${ settings.endpoint }lockers/${ lockerId }/cells/${ cellId }/restrictions/serviceProviders/${ spId }`,
        method: 'DELETE',
      }),
      commit,
      'lockerUpdated',
    ),
  createModule: ({ commit }, {lockerId, block, direction, cells, onlyForSp}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/cells',
      method: 'POST',
      data: {
        block,
        direction,
        cells,
        onlyForSp
      },
    }),
    commit,
    'lockerLoaded'
  ),
  setModuleOrder: ({ commit }, {lockerId, cellIds, order}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/cells/order',
      method: 'PUT',
      data: { cells: cellIds, order: order || 0 }
    }),
    commit,
    'lockerUpdated'
  ),
  hideModule: ({ commit }, {lockerId, cellIds, hidden}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/cells/hidden',
      method: 'PUT',
      data: { cells: cellIds, blocked: !!hidden }
    }),
    commit,
    'lockerUpdated'
  ),
  rebootLocker: ({ commit }, {lockerId}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/reboot/',
      method: 'POST'
    }),
    commit,
    'rebootLocker',
    null,
    false
  ),
  pushConfiguration: ({ commit }, {lockerId, machine_number}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/push-config', //?? ask for correct endpoint
      method: 'POST',
      data: {
        machine_number
      }
    }),
    commit,
    'pushConfiguration',
    null,
    false
  ),
  changeExternalName: ({ commit }, {lockerId, externalName}): any => responseHandler.handle(
    () => axios({
      url: `${settings.endpoint}lockers/${lockerId}/external-name`,
      method: 'PUT',
      data: {
        external_name: externalName
      }
    }),
    commit,
    // 'lockerLoaded'
    'lockerUpdated'
  ),
  takeScreenShot: ({ commit }, {lockerId}): any => responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'lockers/' + lockerId + '/screenshot/',
        method: 'POST'
      }),
      commit,
      'takeScreenShot',
      null,
      false
  ),
  setFeature: ({ commit }, {lockerId, data}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/features',
      method: 'POST',
      data: data
    }),
    commit,
    'lockerToggleLoaded',
  ),

  screenShotToken: ({ commit }, {lockerId}): any => responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'lockers/' + lockerId + '/screenshot/token',
        method: 'GET'
      }),
      commit,
      'screenShotToken',
  ),
  availableCells: ({commit}, lockerId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/cells/available',
      method: 'GET'
    }),
    commit,
    'availableCellsLoaded'
  ),
  fetchLockersOrders: ({commit}, lockerId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/orders',
      method: 'GET',
    }),
    commit,
    'lockersOrdersLoaded',
  ),
  fetchPackagesToTransfer: ({commit}, lockerId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/packagesToTransfer/',
      method: 'GET',
    }),
    commit,
    'packagesToTransferLoaded'
  ),
  fetchLockersList: ({commit}, filters: any): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/install-all',
      method: 'GET',
      params: filters,
    }),
    commit,
    'lockersListLoaded'
  ),
  clearLockersList: ({commit}): any => commit('clearLockersList'),
  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  },
  clearErrors: ({commit}): any => commit('cleanErrors'),
};

const mutations: MutationTree<LockerState> = {
  lockerLoaded(state, payload: ILocker) {
    state.locker = payload;
  },
  lockerToggleLoaded(state, payload: ILocker) {
     state.locker = payload;
        let lockerToUpdate = state.lockers.find(each => each.id === payload.id);
        if (lockerToUpdate) {
            Object.assign(lockerToUpdate, payload);
        }
    },
  // instead of duplicating, let's make a common mutation for similar use cases
  lockerUpdated(state, payload: ILocker) {
    state.locker = payload;
  },
  lockerScreenConfigSet(state, payload) {
    if (state.locker) {
      Object.assign(state.locker, {screenConfig: payload});
    }
  },
  lockersLoaded(state, payload: IShortLocker[]) {
    state.lockers = payload;
  },
  credentialsLoaded(state, payload: any) {
    state.credentials = payload;
  },
  availableCellsLoaded(state, payload: any) {
    state.availableCells = payload;
  },
  screenShotToken(state, payload: any) {
    state.screenShotToken = payload.token;
  },
  lockersOrdersLoaded(state, payload: IListOrder[]) {
    state.lockersOrders = payload;
  },
  lockersListLoaded(state, payload: any) {
    if (!payload) {
      state.lockersList = [];
      return;
    }
    if (payload.data !== undefined && Array.isArray(payload.data)) {
      state.lockersList = payload.data;
      state.lockersListCurrentPage = payload.current_page || PAGINATION_FIRST_PAGE;
      state.lockersListLastPage = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
      state.lockersListTotal = payload.total || PAGINATION_DEFAULT_TOTAL;
      state.lockersListPerPage = payload.per_page || PAGINATION_DEFAULT_PER_PAGE;
    } else if (Array.isArray(payload)) {
      state.lockersList = payload;
    }
  },
  clearLockersList(state) {
    state.lockersList = [];
    state.lockersListCurrentPage = PAGINATION_FIRST_PAGE;
    state.lockersListLastPage = PAGINATION_DEFAULT_LAST_PAGE;
    state.lockersListTotal = PAGINATION_DEFAULT_TOTAL;
    state.lockersListPerPage = PAGINATION_DEFAULT_PER_PAGE;
  },
  packagesToTransferLoaded(state, payload: any) {
    state.packagesToTransfer = payload;
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
  cleanErrors(state) {
    state.errors = {};
  },
};

const namespaced: boolean = true;

export const lockerModule: Module<LockerState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
